import React from "react";
import styled from "styled-components";
import Tile from "../Tile";
import { DEFAULT_TILE_WIDTH, DEFAULT_TILE_HEIGHT } from "../Tile/Tile";
import { spriteKey } from "../../engine/solitaire";

// Vertical cascade spacings. Face-down cards sit nearly on top of each other
// (just enough lip to hint at depth); face-up cards fan wider so rank/suit
// stay legible as the run grows.
const FACE_DOWN_OFFSET = 4;
const FACE_UP_OFFSET = 16;

// onCardClick(depth) fires for face-up cards; depth 0 is the visually-top
// card, deeper depths reach further down the run. onEmptyClick fires when
// the whole column is empty and the player is trying to drop a king.
// selectedFromDepth: if non-null, face-up cards at depth <= selectedFromDepth
// render with the selection glow (the run that would move if the selection
// were dropped somewhere). cardBack names the face-down sprite.
function TableauStack({
  faceDown = [],
  faceUp = [],
  cardBack,
  onCardClick,
  onEmptyClick,
  selectedFromDepth = null,
  // e.g. "t:3" — stamped as data-card-source="t:3:<depth>" on face-up
  // tiles so the drag layer can identify grab targets. Omitted for
  // spectated boards.
  sourceTag,
}) {
  const isEmpty = faceDown.length === 0 && faceUp.length === 0;

  const positioned = [];
  let y = 0;
  let lastTop = 0;
  for (let i = 0; i < faceDown.length; i++) {
    positioned.push({ key: `d${i}`, name: cardBack, top: y });
    lastTop = y;
    y += FACE_DOWN_OFFSET;
  }
  for (let i = 0; i < faceUp.length; i++) {
    const depth = faceUp.length - 1 - i;
    const selected = selectedFromDepth != null && depth <= selectedFromDepth;
    positioned.push({
      key: `u${i}`,
      name: spriteKey(faceUp[i]),
      top: y,
      depth,
      selected,
    });
    lastTop = y;
    y += FACE_UP_OFFSET;
  }
  const height =
    positioned.length > 0 ? lastTop + DEFAULT_TILE_HEIGHT : DEFAULT_TILE_HEIGHT;

  return (
    <Wrapper style={{ height }}>
      {positioned.map((p) =>
        p.depth != null ? (
          <Positioned key={p.key} style={{ top: p.top }}>
            <Tile
              name={p.name}
              as="button"
              onClick={onCardClick ? () => onCardClick(p.depth) : undefined}
              selected={p.selected}
              data-card-source={
                sourceTag ? `${sourceTag}:${p.depth}` : undefined
              }
            />
          </Positioned>
        ) : (
          <Positioned key={p.key} style={{ top: p.top }}>
            <Tile name={p.name} />
          </Positioned>
        ),
      )}
      {isEmpty && onEmptyClick && (
        <EmptyClickArea
          type="button"
          onClick={onEmptyClick}
          aria-label="Empty column"
        />
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
  width: ${DEFAULT_TILE_WIDTH}px;
`;

const Positioned = styled.div`
  position: absolute;
  left: 0;
`;

// Invisible full-size click target for empty columns — the only affordance
// is "something can land here." No glow, no hover border; it's a drop zone.
// Only rendered when Board passes onEmptyClick (i.e., a selection exists),
// so the pointer cursor only shows during drop mode.
const EmptyClickArea = styled.button`
  position: absolute;
  inset: 0;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  outline: none;
  box-shadow: none;
  cursor: pointer;

  &:focus,
  &:hover,
  &:active {
    outline: none !important;
    box-shadow: none !important;
  }
`;

export default TableauStack;
